import { add } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { formatNumberAsPercent, formatNumberAsPrice } from '@shared/common'
import { DEFAULT_TIMEZONE, getTzDateInUtc } from '@shared/dates'
import { getCycleDurationInDays, membershipCycle, membershipCycleForDate } from '@shared/memberships'
import type {
  TBenefitConsumptionRecord,
  TBenefitOffer,
  TLocationUserProfileMembership,
  TMembershipBenefit,
  TMembershipCycle,
} from './types'

import { BenefitAdminDto, BenefitClientDto } from 'service-api/src/types/api'

export const areRequestOptionsEqual = <T extends object>(first: T, second: T) => {
  const keys = new Set([...Object.keys(first), ...Object.keys(second)])
  return [...keys].every((key) => {
    const optionKey = key as keyof T
    return first[optionKey] === second[optionKey]
  })
}

export const getBenefitDiscount = (benefit: BenefitAdminDto | BenefitClientDto) => {
  if (benefit.priceType === 'RATE') {
    return `${benefit.amount ? formatNumberAsPercent(benefit.amount * 100, { keepZero: false }) : ''}`
  } else {
    return `${benefit.amount ? formatNumberAsPrice(benefit.amount, undefined, undefined, false) : ''}`
  }
}

export const isConsumable = (type?: TMembershipBenefit) => type === 'CONSUMABLE'

export const sortBenefits = (benefits: BenefitAdminDto[] | BenefitClientDto[]) => {
  return [...benefits].sort((a, b) => {
    if (isConsumable(a.type) && !isConsumable(b.type)) {
      return -1
    }
    if (!isConsumable(a.type) && isConsumable(b.type)) {
      return 1
    }
    if (!isConsumable(a.type) && !isConsumable(b.type) && a.amount && b.amount) {
      return a.amount - b.amount
    }
    if (a.qty && b.qty) {
      return a.qty - b.qty
    }
    return 0
  })
}

export const getBenefitOffer = (benefit: BenefitAdminDto | BenefitClientDto, offerId: string) => {
  return benefit.benefitOffers?.find((benefitOffer) => {
    const benefitOfferId = benefitOffer.offer?.id || benefitOffer.offerId
    return benefitOfferId === offerId
  })
}

export const getBenefitOfferLabel = (benefit: TBenefitOffer, offerId: string) =>
  `${getBenefitOffer(benefit, offerId || '')?.offer?.displayName || ''} - ${getBenefitDiscount(benefit)}`

export const getBenefitOffers = (locationUserProfileMembership: TLocationUserProfileMembership) => {
  const offers = new Map<string, string>()
  locationUserProfileMembership?.locationMembership?.benefits?.forEach(
    (benefit: BenefitAdminDto | BenefitClientDto) => {
      if (benefit.type === 'CONSUMABLE') {
        benefit.benefitOffers?.forEach((offer) => {
          const offerId = offer.offer?.id || offer.offerId
          if (!offerId) return
          offers.set(offerId, getBenefitOfferLabel(benefit, offerId))
        })
      }
    }
  )
  return offers
}

export const isUnlimitedBenefitsOnly = (benefits: BenefitAdminDto[] | BenefitClientDto[]) => {
  return benefits.every((benefit) => !isConsumable(benefit.type))
}

export const getMembershipYears = (locationUserProfileMembership: TLocationUserProfileMembership) => {
  const startDate = locationUserProfileMembership?.startDate
  if (!startDate) return []

  const startYear = Number(startDate.substring(0, 4))
  const endYear = new Date().getFullYear() + 1
  return [startYear, endYear]
}

export const getYears = (locationUserProfileMembership: TLocationUserProfileMembership) => {
  const [startYear, endYear] = getMembershipYears(locationUserProfileMembership)
  const years: string[] = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year.toString())
  }
  return years
}

export const getCycles = (
  locationUserProfileMembership: TLocationUserProfileMembership,
  invoiceFilterDates?: { startDate: string; endDate: string },
  timezone = DEFAULT_TIMEZONE
) => {
  const billingCycle = locationUserProfileMembership?.locationMembership?.membership?.billingCycle
  const cycles = new Map<string, TMembershipCycle[]>()

  if (!billingCycle) return cycles

  const cycleDurationInDays = getCycleDurationInDays(billingCycle)
  if (cycleDurationInDays === null) return cycles

  const { startDate, endDate } = invoiceFilterDates || {}
  const invoiceStartDate = startDate ? getTzDateInUtc(startDate, null, timezone) : undefined

  const membershipStartDate = getTzDateInUtc(locationUserProfileMembership?.startDate, null, timezone)
  const currentCycle = membershipCycle(locationUserProfileMembership, billingCycle, timezone)

  // if no records exist, return the current cycle
  if (!invoiceFilterDates?.startDate && !invoiceFilterDates?.endDate) {
    const currentYear = currentCycle.startDate.getFullYear().toString()
    cycles.set(currentYear, [currentCycle])
    return cycles
  }

  const [startYear, endYear] = getMembershipYears(locationUserProfileMembership)

  for (let year = startYear; year <= endYear; year++) {
    const yearCycles: TMembershipCycle[] = []
    const yearEnd = getTzDateInUtc(`${year}-12-31`, null, timezone)
    let invoiceEndDate = invoiceFilterDates?.endDate ? getTzDateInUtc(endDate, null, timezone) : yearEnd

    const currentCycleStartYear = parseInt(formatInTimeZone(currentCycle.startDate, timezone, 'yyyy'))
    if (currentCycle.endDate.getTime() > invoiceEndDate.getTime() && currentCycleStartYear === year) {
      invoiceEndDate = currentCycle.endDate
    }

    let currentCycleStartAfter = membershipStartDate
    while (currentCycleStartAfter.getTime() <= invoiceEndDate.getTime()) {
      const cycleEnd = add(currentCycleStartAfter, { days: cycleDurationInDays - 1 })
      const cycleStartYear = parseInt(formatInTimeZone(currentCycleStartAfter, timezone, 'yyyy'))

      if (cycleStartYear > year) {
        break
      }

      const isCurrentCycle =
        currentCycleStartAfter.getTime() === currentCycle.startDate.getTime() &&
        cycleEnd.getTime() === currentCycle.endDate.getTime()
      if (
        cycleStartYear === year &&
        (isCurrentCycle || !invoiceStartDate || cycleEnd.getTime() >= invoiceStartDate.getTime())
      ) {
        yearCycles.push({
          startDate: currentCycleStartAfter,
          endDate: cycleEnd,
        })
      }

      currentCycleStartAfter = add(currentCycleStartAfter, { days: cycleDurationInDays })
    }

    if (yearCycles.length > 0) {
      cycles.set(
        year.toString(),
        yearCycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
      )
    }
  }
  return cycles
}

export const formatCycleLabel = (cycle?: TMembershipCycle, timezone = DEFAULT_TIMEZONE, format = 'MM/dd/yyyy') => {
  if (!cycle) {
    return ''
  }
  return `${formatInTimeZone(cycle.startDate, timezone, format)} - ${formatInTimeZone(cycle.endDate, timezone, format)}`
}

export const getBCRMembershipCycleForDate = (
  benefitConsumptionRecord: TBenefitConsumptionRecord,
  timezone = DEFAULT_TIMEZONE
) => {
  const lupMembership = benefitConsumptionRecord.locationUserProfileMembership
  const cycle = membershipCycleForDate(
    lupMembership,
    lupMembership?.membership?.billingCycle,
    timezone,
    getTzDateInUtc(benefitConsumptionRecord.invoiceStartDate, null, timezone)
  )
  if (!cycle) return undefined
  return {
    startDate: cycle.startDate,
    endDate: cycle.endDate,
  }
}

export const getUniqueBenefits = (benefitConsumptionRecords?: TBenefitConsumptionRecord[]): BenefitAdminDto[] => {
  const benefits = new Map()
  benefitConsumptionRecords?.forEach((r) => {
    if (!r.benefit?.id) return
    benefits.set(r.benefit?.id, r.benefit)
  })
  return Array.from(benefits.values()) as BenefitAdminDto[]
}
